import { useEffect, useState } from "react";
import { Activity, TrendingUp, Eye, Trophy } from "lucide-react";
import { api } from "@/lib/api";

const formatFJD = (n) => {
    if (!n || n <= 0) return "FJ$0";
    if (n >= 1_000_000) return `FJ$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M+`;
    if (n >= 1_000) return `FJ$${Math.round(n / 1_000)}k+`;
    return `FJ$${n}`;
};

const formatCount = (n, suffix = "") => {
    if (n == null) return "—";
    if (n <= 0) return "0";
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k${suffix}`;
    return `${n}${suffix}`;
};

function PulseStat({ icon: Icon, label, value, formatter, testid }) {
    return (
        <div
            data-testid={testid}
            className="flex items-center gap-3 px-5 py-4 rounded-xl bg-card/80 backdrop-blur border border-border hover:border-turquoise/60 transition-colors animate-fade-up"
        >
            <div className="h-10 w-10 rounded-lg bg-turquoise/15 text-turquoise grid place-items-center flex-shrink-0">
                <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
                <div className="font-display text-xl sm:text-2xl font-bold tabular-nums text-foreground leading-none">
                    {formatter(value)}
                </div>
                <div className="mt-1 text-[11px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground font-app font-medium">
                    {label}
                </div>
            </div>
        </div>
    );
}

export default function MarketplacePulse() {
    const [pulse, setPulse] = useState(null);

    useEffect(() => {
        let mounted = true;
        const fetchPulse = async () => {
            try {
                const { data } = await api.get("/public/pulse");
                if (mounted) setPulse(data);
            } catch (_e) {
                /* fail silent — widget hides if API unavailable */
            }
        };
        fetchPulse();
        const interval = setInterval(fetchPulse, 60_000); // refresh every 60s
        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, []);

    if (!pulse) return null;

    return (
        <section
            data-testid="marketplace-pulse-section"
            className="relative -mt-8 sm:-mt-10 z-20 px-4 sm:px-6 lg:px-8"
        >
            <div className="max-w-7xl mx-auto">
                <div className="bg-card border border-border rounded-2xl shadow-xl px-4 sm:px-6 py-4 sm:py-5">
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fiji-green opacity-75" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-fiji-green" />
                        </span>
                        <span className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-app font-semibold text-fiji-green dark:text-turquoise">
                            Marketplace Pulse · Live
                        </span>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <PulseStat
                            icon={Activity}
                            label="Active Joint Ventures"
                            value={pulse.active_jvs}
                            formatter={(v) => formatCount(v, pulse.active_jvs >= 5 ? "+" : "")}
                            testid="pulse-active-jvs"
                        />
                        <PulseStat
                            icon={TrendingUp}
                            label="Capital Sought"
                            value={pulse.total_capital_sought_fjd}
                            formatter={formatFJD}
                            testid="pulse-capital-sought"
                        />
                        <PulseStat
                            icon={Eye}
                            label="Investor Watchlist"
                            value={pulse.investor_watchlist}
                            formatter={(v) => formatCount(v, pulse.investor_watchlist >= 10 ? "+" : "")}
                            testid="pulse-watchlist"
                        />
                        <PulseStat
                            icon={Trophy}
                            label="Closed Deals"
                            value={pulse.closed_deals}
                            formatter={(v) => formatCount(v)}
                            testid="pulse-closed-deals"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
